import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RANKING_PLATFORMS, fromLocalDatetime, rankingReportError, toLocalDatetime, type RankingReportInput } from '@/lib/campaigns/rankingCompetition';
import type { RankingReport } from '@/queries/cpmRanking';
import { isManualFacebookReport } from '@/lib/campaigns/manualFacebookReport';

export function RankingReportForm({ initialReport, allowedPlatforms, onSubmit, onCancel }: {
  initialReport?: RankingReport;
  allowedPlatforms?: string[] | null;
  onSubmit: (input: RankingReportInput) => Promise<void>;
  onCancel?: () => void;
}) {
  const { t } = useTranslation();
  const platforms = RANKING_PLATFORMS.filter(platform => !allowedPlatforms?.length || allowedPlatforms.includes(platform));
  const [input, setInput] = useState<RankingReportInput>(() => initialReport ? {
    id: initialReport.id, platform: initialReport.platform, url: initialReport.url, profile_url: initialReport.profile_url,
    published_at: initialReport.published_at, reported_views: initialReport.reported_views, evidence_url: initialReport.evidence_url,
  } : { platform: platforms[0] ?? 'instagram', url: '', profile_url: '', published_at: '', reported_views: '', evidence_url: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // A period report is never linked to a connected Page, so a Facebook report stays manual.
  const manualFacebook = isManualFacebookReport({ platform: input.platform });
  return <form className="space-y-4" onSubmit={async event => {
    event.preventDefault();
    if (saving) return;
    const code = rankingReportError(input);
    if (code) { setError(code); return; }
    setSaving(true); setError(null);
    try {
      await onSubmit(input);
      if (!initialReport) setInput(previous => ({ ...previous, url: '', published_at: '', reported_views: '', evidence_url: '' }));
    } catch { setError('submitFailed'); } finally { setSaving(false); }
  }}>
    <fieldset className="space-y-4" disabled={saving}>
      <legend className="mb-3 font-semibold">{t(initialReport ? 'ranking.editReport' : 'ranking.submitReport')}</legend>
      <p className="text-sm text-muted-foreground">{t('ranking.periodEvidenceHelp')}</p>
      <div className="space-y-2">
        <Label htmlFor="ranking-platform">{t('ranking.platform')}</Label>
        <Select value={input.platform} onValueChange={value => setInput({ ...input, platform: value as RankingReportInput['platform'] })}>
          <SelectTrigger id="ranking-platform"><SelectValue /></SelectTrigger>
          <SelectContent>{platforms.map(platform => <SelectItem key={platform} value={platform}>{t(`ranking.platforms.${platform}`)}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <p className="text-sm text-muted-foreground">{t(manualFacebook ? 'ranking.facebookReportHelp' : 'ranking.officialOwnershipHelp')} {!manualFacebook && <button type="button" className="underline" onClick={() => window.dispatchEvent(new CustomEvent('autoclipper:open-account-settings', { detail: { section: 'social', platform: input.platform } }))}>{t('ranking.connectAccount')}</button>}</p>
      {(['url', 'profile_url', 'evidence_url'] as const).map(key => <div key={key} className="space-y-2">
        <Label htmlFor={`ranking-report-${key}`}>{t(`ranking.reportFields.${key}`)}</Label>
        <Input id={`ranking-report-${key}`} type="url" required maxLength={2048} value={input[key]} onChange={event => setInput({ ...input, [key]: event.target.value })} placeholder="https://" />
      </div>)}
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2"><Label htmlFor="ranking-published">{t('ranking.reportFields.published_at')}</Label><Input id="ranking-published" type="datetime-local" required value={toLocalDatetime(input.published_at)} onChange={event => setInput({ ...input, published_at: fromLocalDatetime(event.target.value) ?? '' })} /><p className="text-xs text-muted-foreground">{t('ranking.localTimeHelp')}</p></div>
        <div className="space-y-2"><Label htmlFor="ranking-reported-views">{t('ranking.reportFields.reported_views')}</Label><Input id="ranking-reported-views" inputMode="numeric" pattern="[0-9]+" required value={input.reported_views} onChange={event => setInput({ ...input, reported_views: event.target.value })} /></div>
      </div>
      {error && <p role="alert" className="text-sm text-destructive">{t(`ranking.errors.${error}`)}</p>}
      <div className="flex gap-3"><Button type="submit">{t(saving ? 'ranking.saving' : 'ranking.sendReport')}</Button>{onCancel && <Button type="button" variant="outline" onClick={onCancel}>{t('ranking.cancel')}</Button>}</div>
    </fieldset>
  </form>;
}
